import { useMutation, useQuery } from '@apollo/client';
import { graphql } from 'gql.tada';

const ScheduleBlockFields = graphql(`
  fragment ScheduleBlockFields on ScheduleBlock {
    id
    title
    displayTitle
    activityKind
    startsAt
    endsAt
    timeLabel
    weekdays
    oneTime
    oneTimeDate
    attendanceEnabled
    notes
    term { id name startsOn endsOn }
    classroom { id name }
    audiences {
      kind
      gradeLevelKey
      classOffering { id displayName term { id name startsOn endsOn } }
      person { id firstName lastName }
    }
    staffAssignments {
      roleLabel
      canTakeAttendance
      staffMember {
        id
        kind
        state
        person { id firstName lastName }
      }
    }
  }
`);

const ScheduleWorkspaceQuery = graphql.persisted(
  "ScheduleWorkspaceQuery",
  graphql(`
    query ScheduleWorkspaceQuery(
      $schoolYearId: ID!
      $date: ISO8601Date!
      $time: String!
      $periodStart: ISO8601Date!
      $periodEnd: ISO8601Date!
      $staffPersonId: ID
      $studentPersonId: ID
      $classroomId: ID
    ) {
      schoolYear(id: $schoolYearId) {
        id
        name
        startDate
        endDate
        terms { id name startsOn endsOn }
        scheduleBlocks { ...ScheduleBlockFields }
        schoolSchedulePeriod: schedulePeriod(startsOn: $periodStart, endsOn: $periodEnd) {
          startsOn
          endsOn
          entries {
            date
            startsAt
            endsAt
            timeLabel
            entryType
            title
            classroom { id name }
            classOffering { id displayName term { id name startsOn endsOn } }
            scheduleBlock { id attendanceEnabled }
            studentPeople { id }
            staffPeople { id }
          }
        }
        scheduleSnapshot(
          date: $date
          time: $time
          staffPersonId: $staffPersonId
          studentPersonId: $studentPersonId
          classroomId: $classroomId
        ) {
          occurrences {
            date
            startsAt
            endsAt
            timeLabel
            state
            classroom { id name }
            scheduleBlock { ...ScheduleBlockFields }
          }
        }
        studentEnrollments {
          id
          state
          gradeLevel
          person { id firstName lastName }
        }
        courses {
          id
          name
          classOfferings {
            id
            section
            displayName
            term { id name startsOn endsOn }
            rosterEntries {
              state
              person { id firstName lastName }
            }
          }
        }
        school {
          id
          gradeLevelsSetting { items { description order } }
          classrooms { id name }
          activeStaff: staffMembers(state: ACTIVE) {
            id
            kind
            state
            person { id firstName lastName }
          }
          inactiveStaff: staffMembers(state: INACTIVE) {
            id
            kind
            state
            person { id firstName lastName }
          }
        }
      }
    }
  `, [ScheduleBlockFields])
);

const CreateScheduleBlockMutation = graphql.persisted(
  "CreateScheduleBlock",
  graphql(`
    mutation CreateScheduleBlock($input: CreateScheduleBlockInput!) {
      createScheduleBlock(input: $input) {
        scheduleBlock { ...ScheduleBlockFields }
        userErrors { message path }
      }
    }
  `, [ScheduleBlockFields])
);

const CreateClassScheduleSeriesMutation = graphql.persisted(
  "CreateClassScheduleSeries",
  graphql(`
    mutation CreateClassScheduleSeries($input: CreateClassScheduleSeriesInput!) {
      createClassScheduleSeries(input: $input) {
        scheduleBlocks { ...ScheduleBlockFields }
        reused
        userErrors { message path }
      }
    }
  `, [ScheduleBlockFields])
);

const UpdateScheduleBlockMutation = graphql.persisted(
  "UpdateScheduleBlock",
  graphql(`
    mutation UpdateScheduleBlock($input: UpdateScheduleBlockInput!) {
      updateScheduleBlock(input: $input) {
        scheduleBlock { ...ScheduleBlockFields }
        userErrors { message path }
      }
    }
  `, [ScheduleBlockFields])
);

const DeleteScheduleBlockMutation = graphql.persisted(
  "DeleteScheduleBlock",
  graphql(`
    mutation DeleteScheduleBlock($input: DeleteScheduleBlockInput!) {
      deleteScheduleBlock(input: $input) {
        success
        userErrors { message path }
      }
    }
  `)
);

const UpsertScheduleBlockOccurrenceMutation = graphql.persisted(
  "UpsertScheduleBlockOccurrence",
  graphql(`
    mutation UpsertScheduleBlockOccurrence($input: UpsertScheduleBlockOccurrenceInput!) {
      upsertScheduleBlockOccurrence(input: $input) {
        scheduleBlock { id }
        userErrors { message path }
      }
    }
  `)
);

const DeleteScheduleBlockOccurrenceMutation = graphql.persisted(
  "DeleteScheduleBlockOccurrence",
  graphql(`
    mutation DeleteScheduleBlockOccurrence($input: DeleteScheduleBlockOccurrenceInput!) {
      deleteScheduleBlockOccurrence(input: $input) {
        scheduleBlock { id }
        userErrors { message path }
      }
    }
  `)
);

const CopyYearScheduleMutation = graphql.persisted(
  "CopyYearSchedule",
  graphql(`
    mutation CopyYearSchedule($input: CopyYearScheduleInput!) {
      copyYearSchedule(input: $input) {
        copiedBlocks { id displayTitle }
        unmatched { blockId title reason }
        userErrors { message path }
      }
    }
  `)
);

export {
  ScheduleBlockFields,
  ScheduleWorkspaceQuery,
  CreateScheduleBlockMutation,
  CreateClassScheduleSeriesMutation,
  UpdateScheduleBlockMutation,
  DeleteScheduleBlockMutation,
  UpsertScheduleBlockOccurrenceMutation,
  DeleteScheduleBlockOccurrenceMutation,
  CopyYearScheduleMutation,
};

interface ScheduleFilters {
  staffPersonId?: string;
  studentPersonId?: string;
  classroomId?: string;
}

export function useScheduleWorkspaceQuery(
  schoolYearId?: string,
  date?: string,
  time?: string,
  periodStart?: string,
  periodEnd?: string,
  filters: ScheduleFilters = {},
) {
  return useQuery(ScheduleWorkspaceQuery, {
    variables: {
      schoolYearId: schoolYearId!,
      date: date!,
      time: time!,
      periodStart: periodStart!,
      periodEnd: periodEnd!,
      staffPersonId: filters.staffPersonId,
      studentPersonId: filters.studentPersonId,
      classroomId: filters.classroomId,
    },
    skip: !schoolYearId || !date || !time || !periodStart || !periodEnd,
    fetchPolicy: 'cache-and-network',
    // A same-variable refetch must publish loading=true so print actions never
    // snapshot the pre-mutation period while the committed schedule refreshes.
    notifyOnNetworkStatusChange: true,
  });
}

export const useCreateScheduleBlock = () => useMutation(CreateScheduleBlockMutation);
export const useCreateClassScheduleSeries = () => useMutation(CreateClassScheduleSeriesMutation);
export const useUpdateScheduleBlock = () => useMutation(UpdateScheduleBlockMutation);
export const useDeleteScheduleBlock = () => useMutation(DeleteScheduleBlockMutation);
export const useUpsertScheduleBlockOccurrence = () => useMutation(UpsertScheduleBlockOccurrenceMutation);
export const useDeleteScheduleBlockOccurrence = () => useMutation(DeleteScheduleBlockOccurrenceMutation);
export const useCopyYearSchedule = () => useMutation(CopyYearScheduleMutation);
